import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  SendOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { eventLabel, statusLabel } from "@wms/domain";
import type { Dashboard } from "@wms/domain";
import { Alert, Card, Col, List, Row, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuthStore } from "../auth/store";
import { MetricTile, type MetricProps } from "../components/MetricTile";
import { PageHeader } from "../components/PageHeader";
import { StaffDesktop } from "../workspace/StaffDesktop";

/** `op`: a tile of documents takes the colour of their operation (поступления зелёные, заказы на отгрузку синие). */
function Metric(props: MetricProps) {
  return (
    <Col xs={12} sm={8} xl={4}>
      <MetricTile {...props} />
    </Col>
  );
}

/**
 * ТЗ «Рабочие столы» 31.10.2026: with «use_personal_workspace» the desktop is built from widgets (StaffDesktop); with
 * the option off — exactly the page of HEAD (LegacyHome).
 */
export function HomePage() {
  const personal = useAuthStore((s) => s.option("use_personal_workspace"));
  return personal ? <StaffDesktop legacy={<LegacyHome />} /> : <LegacyHome />;
}

export function LegacyHome() {
  const { me, can } = useAuthStore();
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<Dashboard>("/dashboard"),
    enabled: can("analytics.dashboard.read"),
    refetchInterval: 60000,
  });
  const d = dashboard.data;
  const hour = new Date().getHours();
  const greeting = hour < 6 ? "Доброй ночи" : hour < 12 ? "Доброе утро" : hour < 18 ? "Добрый день" : "Добрый вечер";
  return (
    <div>
      <PageHeader
        title={`${greeting}, ${me?.user.full_name?.split(" ")[0] ?? ""}`}
        subtitle={`${dayjs().format("dddd, D MMMM YYYY")}${d?.timezone ? ` · «сегодня» в показателях — по времени склада (${d.timezone})` : ""}`}
      />
      {!can("analytics.dashboard.read") && <Typography.Paragraph type="secondary">Показатели рабочего стола недоступны для вашей роли.</Typography.Paragraph>}
      {d && (
        <>
          <Row gutter={[14, 14]} style={{ marginBottom: 18 }}>
            <Metric op="receive" title="Поступлений на сегодня" hint="Документы поступления с датой «сегодня», кроме отменённых" value={d.receipts.planned_today} to="/expected_receipts" icon={<InboxOutlined />} />
            <Metric op="receive" title="Приёмка в работе" value={d.receipts.in_progress} to="/expected_receipts" icon={<PlayCircleOutlined />} tone="orange" />
            <Metric op="receive" title="Принято сегодня" hint="Поступления, приёмка которых завершена сегодня" value={d.receipts.accepted_today} to="/expected_receipts" icon={<CheckCircleOutlined />} tone="green" />
            <Metric op="ship" title="Заказов на сегодня" hint="Заказы на отгрузку с датой «сегодня», кроме отменённых" value={d.orders.planned_today} to="/shipment_orders" icon={<SendOutlined />} tone="violet" />
            <Metric op="ship" title="Заказы в отборе" value={d.orders.picking} to="/shipment_orders" icon={<ThunderboltOutlined />} tone="orange" />
            <Metric op="ship" title="Отгружено сегодня" hint="Заказы, отгруженные сегодня" value={d.orders.shipped_today} to="/shipment_orders" icon={<CheckCircleOutlined />} tone="green" />
            <Metric title="Задач к выполнению" value={d.tasks.ready} to="/dispatcher" icon={<ClockCircleOutlined />} />
            <Metric title="Задач выполняется" value={d.tasks.in_progress} to="/dispatcher" icon={<PlayCircleOutlined />} tone="orange" />
            <Metric title="Задач с проблемой" value={d.tasks.problem} to="/dispatcher" icon={<WarningOutlined />} tone={d.tasks.problem ? "red" : "gray"} />
            <Metric title="Выполнено сегодня" value={d.tasks.done_today} to="/reports/productivity" icon={<CheckCircleOutlined />} tone="green" />
            <Metric title="Исполнителей на смене" value={d.tasks.executors} to="/dispatcher" icon={<TeamOutlined />} tone="violet" />
            <Metric title="Товаров на остатке" value={d.stock.objects} to="/stock-summary" icon={<DatabaseOutlined />} tone="gray" suffix={`в ${d.stock.locations} яч.`} />
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title={<span><AlertOutlined style={{ marginRight: 8, color: "#d98c1f" }} />Требует внимания</span>} size="small">
                {d.alerts.length === 0 && (
                  <div style={{ padding: "18px 0", textAlign: "center", color: "var(--wms-muted)" }}>
                    <CheckCircleOutlined style={{ fontSize: 28, color: "#1f9d55", display: "block", marginBottom: 8 }} />
                    Всё спокойно
                  </div>
                )}
                {d.alerts.map((a) => (
                  <Alert
                    key={a.kind}
                    type={a.level === "danger" ? "error" : a.level}
                    showIcon
                    style={{ marginBottom: 8, borderRadius: 10 }}
                    message={a.link.startsWith("/reports") && !can("analytics.reports.read") ? a.text : <Link to={a.link}>{a.text}</Link>}
                  />
                ))}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Последние события" size="small" extra={<Link to="/reports">Отчёты</Link>}>
                <List
                  size="small"
                  dataSource={d.recent_events}
                  renderItem={(e) => (
                    <List.Item style={{ paddingInline: 0 }}>
                      <Typography.Text type="secondary" style={{ width: 92, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                        {dayjs(e.created_at).format("DD.MM HH:mm")}
                      </Typography.Text>
                      <Tag style={{ marginRight: 8 }}>{eventLabel(e.type)}</Tag>
                      <Typography.Text style={{ flex: 1 }} ellipsis>
                        {String(e.payload.number ?? e.payload.code ?? e.payload.title ?? "")}
                      </Typography.Text>
                      {e.payload.to ? <Typography.Text type="secondary">→ {statusLabel(e.payload.to)}</Typography.Text> : e.payload.status ? <Typography.Text type="secondary">{statusLabel(e.payload.status)}</Typography.Text> : null}
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
